import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import GlowCard from './GlowCard';

const S = '#00F5A0';
const G = '#8892A4';
const DANGER = '#EF4444';

const AlertTriangleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={DANGER} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const Trash2Icon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={DANGER} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const iconContainerStyle = {
  width: 40,
  height: 40,
  borderRadius: 10,
  flexShrink: 0,
  background: 'rgba(239,68,68,0.12)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const primaryButton = {
  background: S,
  color: '#0A1628',
  border: 'none',
  borderRadius: 8,
  padding: '10px 20px',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};

const secondaryButton = {
  background: 'transparent',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 8,
  padding: '10px 20px',
  fontWeight: 600,
  fontSize: 14,
  cursor: 'pointer',
};

const dangerButton = {
  background: DANGER,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 20px',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};

const dangerButtonDisabled = {
  ...dangerButton,
  opacity: 0.5,
  cursor: 'not-allowed',
};

const DeleteAccountModal = ({ isOpen, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [step, setStep] = useState('warning');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const continueButtonRef = useRef(null);
  const passwordInputRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setStep('warning');
      setPassword('');
      setPasswordError('');
      setVerifying(false);
      setDeleteError('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (step === 'warning') continueButtonRef.current?.focus();
    if (step === 'password') passwordInputRef.current?.focus();
    if (step === 'processing' && deleteError) closeButtonRef.current?.focus();
  }, [isOpen, step, deleteError]);

  useEffect(() => {
    if (!isOpen || step === 'processing') return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, step, onClose]);

  if (!isOpen) return null;

  const handleConfirmPassword = async () => {
    if (!password) return;
    setPasswordError('');
    setVerifying(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: user.email,
      password,
    });
    setVerifying(false);

    if (error) {
      setPasswordError('Mot de passe incorrect. Réessayez.');
      return;
    }

    setStep('processing');
    setDeleteError('');
    const { error: invokeError } = await supabase.functions.invoke('delete-account');

    if (invokeError) {
      setDeleteError('Une erreur est survenue. Réessayez dans un instant.');
      return;
    }

    onSuccess();
  };

  const titleId = 'delete-account-modal-title';

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.75)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{ width: '100%', maxWidth: 440 }}
        onClick={(e) => e.stopPropagation()}
      >
        <GlowCard variant="card" style={{ background: '#0A1628', padding: '28px 28px 24px' }}>
          {step === 'warning' && (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
                <div style={iconContainerStyle}>
                  <AlertTriangleIcon />
                </div>
                <h2 id={titleId} style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#fff' }}>
                  Supprimer définitivement votre compte
                </h2>
              </div>

              <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: DANGER }}>
                Cette action est irréversible.
              </p>
              <p style={{ margin: '0 0 12px', fontSize: 14, color: G, lineHeight: 1.6 }}>
                Conformément à votre droit à l'effacement (RGPD article 17), vous êtes sur le
                point de demander la suppression complète de votre compte SafeHeir. En
                supprimant votre compte, vous perdrez définitivement :
              </p>
              <div style={{ margin: '0 0 16px', fontSize: 14, color: G, lineHeight: 1.7 }}>
                <p style={{ margin: '0 0 6px' }}>1. Votre coffre-fort et tous les items qu'il contient</p>
                <p style={{ margin: '0 0 6px' }}>2. Vos bénéficiaires désignés et leurs consentements</p>
                <p style={{ margin: '0 0 6px' }}>3. Votre historique de check-in</p>
                <p style={{ margin: 0 }}>4. Votre journal d'activité</p>
              </div>
              <p style={{ margin: '0 0 10px', fontSize: 14, color: G, lineHeight: 1.6 }}>
                Aucune récupération ne sera possible, même par le support SafeHeir.
              </p>
              <p style={{ margin: '0 0 24px', fontSize: 14, color: G, lineHeight: 1.6 }}>
                Assurez-vous d'avoir exporté vos données avant de continuer.
              </p>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button style={secondaryButton} onClick={onClose}>
                  Annuler
                </button>
                <button
                  ref={continueButtonRef}
                  style={primaryButton}
                  onClick={() => setStep('password')}
                >
                  Continuer
                </button>
              </div>
            </>
          )}

          {step === 'password' && (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 }}>
                <div style={iconContainerStyle}>
                  <Trash2Icon />
                </div>
                <div>
                  <h2 id={titleId} style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: '#fff' }}>
                    Confirmer votre identité
                  </h2>
                  <p style={{ margin: 0, fontSize: 14, color: G, lineHeight: 1.6 }}>
                    Saisissez votre mot de passe actuel pour confirmer la suppression.
                  </p>
                </div>
              </div>

              <label
                htmlFor="delete-account-password"
                style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 6 }}
              >
                Mot de passe
              </label>
              <input
                ref={passwordInputRef}
                id="delete-account-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  padding: '10px 12px',
                  fontSize: 14,
                  color: '#fff',
                  marginBottom: 8,
                  boxSizing: 'border-box',
                }}
              />
              {passwordError && (
                <p style={{ margin: '0 0 16px', fontSize: 13, color: DANGER }}>{passwordError}</p>
              )}
              {!passwordError && <div style={{ marginBottom: 16 }} />}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button style={secondaryButton} onClick={() => setStep('warning')}>
                  Retour
                </button>
                <button
                  style={!password || verifying ? dangerButtonDisabled : dangerButton}
                  disabled={!password || verifying}
                  onClick={handleConfirmPassword}
                >
                  Supprimer définitivement mon compte
                </button>
              </div>
            </>
          )}

          {step === 'processing' && (
            <>
              <h2 id={titleId} style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: '#fff' }}>
                {deleteError ? 'Suppression impossible' : 'Suppression en cours'}
              </h2>
              <p style={{ margin: '0 0 20px', fontSize: 14, color: deleteError ? DANGER : G, lineHeight: 1.6 }}>
                {deleteError || 'Suppression en cours...'}
              </p>
              {deleteError && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button ref={closeButtonRef} style={secondaryButton} onClick={onClose}>
                    Fermer
                  </button>
                </div>
              )}
            </>
          )}
        </GlowCard>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
